import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  startTransition,
} from 'react';
import { useTranslation } from '@aimsos/i18n';
import { useDebounce } from '@aimsos/hooks';

import { listAgents } from '../../../../services';
import { parseAgentListResponse } from '../../../../shared/utils/apiResponse';
import { buildAgentListBody } from '../../constants';
import { mapAgentListItemToUi, normalizePagedMeta } from '../../utils';

/**
 * Converts the UI filter shape into the API `filters` object.
 * Only includes keys that have active values.
 *
 * UI shape: { status: string[], type: string[], category: string[], createdWithin: string|null }
 * API shape: { status?: string[], type?: string[], category?: string[], created_at?: { from_, to } }
 *
 * The date range is the one place the request body is NOT camelCase (CT-5291):
 * every top-level key is aliased to camelCase server-side, but `FilterCriteria`
 * and `DateRange` are plain pydantic models with no alias generator, so the API
 * only reads `created_at` with a `from_` key — and silently ignores anything
 * else, which is why sending `createdAt: { from, to }` returned the whole
 * unfiltered list instead of an error.
 *
 * There is no `updated_at` filter server-side (it is accepted for `orderBy`
 * only), so this range is by creation date — see the UI label.
 */
function buildApiFilters(uiFilters) {
  const out = {};
  if (uiFilters.status?.length) out.status = uiFilters.status;
  if (uiFilters.type?.length) out.type = uiFilters.type;
  if (uiFilters.category?.length) out.category = uiFilters.category;
  if (uiFilters.createdWithin) {
    const now = new Date();
    const from = new Date(now);
    if (uiFilters.createdWithin === 'today') {
      from.setHours(0, 0, 0, 0);
    } else if (uiFilters.createdWithin === 'last7days') {
      from.setDate(now.getDate() - 7);
    } else if (uiFilters.createdWithin === 'last30days') {
      from.setDate(now.getDate() - 30);
    } else if (uiFilters.createdWithin === 'last90days') {
      from.setDate(now.getDate() - 90);
    }
    out.created_at = { from_: from.toISOString(), to: now.toISOString() };
  }
  return out;
}

const DEFAULT_PAGE_SIZE = 15;

const DEFAULT_META = {
  total_items: 0,
  total_pages: 1,
  current_page: 1,
  per_page: DEFAULT_PAGE_SIZE,
  has_next: false,
  has_previous: false,
};

/**
 * Agent list: pagination, debounced search, POST /v1/workspace/agent/list.
 *
 * Response envelope (real API):
 *   { msgCode, requestId, data: [...], meta: { totalItems, totalPages, currentPage, perPage, hasNext, hasPrevious } }
 *
 * Used by {@link AgentsProvider} so API access stays in hooks.
 */
export function useAgentsList() {
  const { t } = useTranslation('agents');
  const [agents, setAgents] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [meta, setMeta] = useState(DEFAULT_META);
  const [listSearchKeyword, setListSearchKeyword] = useState('');
  const debouncedSearch = useDebounce(listSearchKeyword, 350);
  const prevDebouncedSearchRef = useRef(null);
  const [listRefreshKey, setListRefreshKey] = useState(0);
  const [filters, setFilters] = useState({ status: [], type: [], category: [], createdWithin: null });

  const refetchAgents = useCallback(() => {
    setListRefreshKey((k) => k + 1);
  }, []);

  // Reset to page 1 when the debounced search term changes (after first render)
  useEffect(() => {
    if (prevDebouncedSearchRef.current === null) {
      prevDebouncedSearchRef.current = debouncedSearch;
      return;
    }
    if (prevDebouncedSearchRef.current !== debouncedSearch) {
      prevDebouncedSearchRef.current = debouncedSearch;
      startTransition(() => setPage(1));
    }
  }, [debouncedSearch]);

  useEffect(() => {
    let cancelled = false;
    startTransition(() => {
      setListLoading(true);
      setListError(null);
    });

    const apiFilters = buildApiFilters(filters);
    listAgents(buildAgentListBody(page, pageSize, debouncedSearch, apiFilters))
      .then((res) => {
        if (cancelled) return;
        const { rows, meta: rawMeta } = parseAgentListResponse(res, t('list.error'));
        setAgents(rows.map(mapAgentListItemToUi));
        setMeta(normalizePagedMeta(rawMeta, page, pageSize));
      })
      .catch((err) => {
        if (cancelled) return;
        setListError(err?.message ?? t('common.requestFailed'));
        setAgents([]);
        setMeta({ ...DEFAULT_META, current_page: page, per_page: pageSize });
      })
      .finally(() => {
        if (!cancelled) setListLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [page, pageSize, debouncedSearch, filters, listRefreshKey, t]);

  const setPageSizeAndReset = useCallback((nextSize) => {
    setPageSize(nextSize);
    setPage(1);
  }, []);

  const getAgent = useCallback(
    (id) => agents.find((a) => a.id === id) ?? null,
    [agents],
  );

  /**
   * Optimistically update a single agent entry in the list without refetching.
   * Called after a successful save from the edit page so the list (and any
   * consumer reading from context) immediately reflects the new values.
   */
  const updateAgentInList = useCallback((id, updates) => {
    setAgents((prev) =>
      prev.map((a) => (a.id === id ? { ...a, ...updates } : a)),
    );
  }, []);

  return useMemo(
    () => ({
      agents,
      listLoading,
      listError,
      listRefreshKey,
      meta,
      page,
      setPage,
      pageSize,
      setPageSize: setPageSizeAndReset,
      getAgent,
      updateAgentInList,
      listSearchKeyword,
      setListSearchKeyword,
      filters,
      setFilters,
      refetchAgents,
    }),
    [
      agents,
      listLoading,
      listError,
      listRefreshKey,
      meta,
      page,
      pageSize,
      setPageSizeAndReset,
      getAgent,
      updateAgentInList,
      listSearchKeyword,
      filters,
      refetchAgents,
    ],
  );
}
